/** Body of `POST /v1/deferrals/manual` — camelCase, unlike the older endpoints. */
export interface IManualDeferralRequest {
  visitId: string
  /** Length of the deferral in whole days, counted from today. */
  durationDays: number
  /** Free-text justification written by the doctor. */
  note?: string
  organizationId: string
  createdByUserId: string
  /**
   * Empty or `null` — recorded as `THERAPIST_DECISION`. Otherwise a temporary
   * dictionary reason (`is_permanent = false`); a permanent one is refused with
   * 500631, a system or unknown code with 500630.
   */
  reasonCode: string | null
}

export interface IManualDeferralResult {
  visitId: string
  deferred: boolean
  /** ISO date the deferral runs until. */
  deferralUntil: string
  durationDays: number
  reasonCodes: string[]
  permanent: boolean
}

// ── Журнал отводов (BS-237) ──────────────────────────────────────────────────

export type TDeferralType = 'PERMANENT' | 'TEMPORARY'

/**
 * - `ACTIVE` — not lifted, and permanent or ending today or later;
 * - `EXPIRED` — temporary, not lifted, the end date has passed — the donor is
 *   still not admitted until a therapist examines them. It can be lifted too,
 *   which clears the card's EXAMINATION_REQUIRED;
 * - `LIFTED` — lifted by a doctor (`overridden_at` / `override_reason` set).
 */
export type TDeferralStatus = 'ACTIVE' | 'EXPIRED' | 'LIFTED'

/** REGISTRY, QUESTIONNAIRE, DOCTOR, LAB; older rows may carry MYGOV. */
export type TDeferralSource =
  | 'REGISTRY'
  | 'QUESTIONNAIRE'
  | 'DOCTOR'
  | 'LAB'
  | 'MYGOV'

/**
 * One deferral — a row of `GET /v1/deferrals` and an entry of the donor's
 * history. `null` fields are present in the response.
 */
export interface IDeferralJournalItem {
  /** Pass as `deferral_id` to lift the deferral. */
  id: string
  /** Human-readable number, e.g. `07-03-00042-1`. */
  public_id: string
  donor_id: string
  /** The visit the deferral was raised on. */
  visit_id: string
  uid: string
  fio_initials: string
  birth_date: string
  reason_code: string
  /** Localized via Accept-Language; equals the code when the name is unknown. */
  reason_name: string
  type: TDeferralType
  source: TDeferralSource | string
  deferral_from: string
  /** `null` for a permanent deferral. */
  deferral_until: string | null
  /** Days left including the end day; `null` unless ACTIVE and temporary. */
  remaining_days: number | null
  status: TDeferralStatus
  /**
   * `false` once lifted, and for the AIDS / hepatitis registries. Lifting one
   * lifts every active record of it (same visit, same reason).
   */
  is_overridable: boolean
  /**
   * The donor's last visit — in your organization on the list, in any
   * organization in the donor's history.
   */
  last_visit_at: string | null
  overridden_at: string | null
  override_reason: string | null
}

/** Query of `GET /v1/deferrals`; the summary takes the same minus `type`. */
export interface IDeferralListParams {
  type: TDeferralType
  status?: TDeferralStatus
  /** ФИО (Latin or Cyrillic), visit UID or birth date. */
  search?: string
  /** Inclusive bounds on the deferral start, `yyyy-MM-dd`. */
  date_from?: string
  date_to?: string
  page?: number
  size?: number
  sort?: string[]
}

export type IDeferralSummaryParams = Omit<
  IDeferralListParams,
  'type' | 'page' | 'size' | 'sort'
>

/** Number of deferrals (rows, not donors) per sub-tab. */
export interface IDeferralSummary {
  permanent: number
  temporary: number
}

/** Error codes of `POST /v1/deferrals/manual` and the processing deferral. */
export const DEFERRAL_ERROR_CODES = {
  /** A system or unknown reason code. */
  REASON_NOT_ALLOWED: '500630',
  /** The reason is permanent, the deferral temporary (or the other way). */
  REASON_PERMANENCE_MISMATCH: '500631',
} as const
